import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { useAuditory, outAuditory } from "@auditory/provider/hooks";
import { logsConfig } from "@auditory/provider/actions";
import { FooterAction } from "@/components/organisms/FooterAction";
import history from "@/utils/history";
import { LIST_TYPE_ROAD } from "../../../../../../constants/list-type-roads";
import { StateMachine } from "@/utils/consts";
import { saveUSerData, saveLeadData, setHasTaxObligations, setHasAcceptedExclusions } from "@/redux/actions/vida";
import helpers, { sendTagChain } from "@/utils/helpers";
import {
  setJobSelected,
  setWorkStatus,
  setCompanyName,
} from "@/redux/actions/jobs";
import { showModalLoading } from "@/redux/actions/global";
import { useLeadsService } from "@/services/leads.service";
import { selectDomicilio } from "@/redux/actions/domcilio";
import { setViewModalError } from "@/redux/actions/global";
import { usePaymentService } from "@/services/payment.service";
import {
  setBeneficiaryType,
  showModalAddBeneficiaries as showModalAddBeneficiariesAction,
  updateBeneficiaries,
} from "@/redux/actions/beneficiaries";
import { DetailPlanMobile } from "../../../shared/components/DetailPlanMobile/index";
import { useQuoteService } from "@/services/quote.service";
import { getProductOptions } from "@/utils/product";
import { focusFirstErrorField } from '@/utils/Utils'
import url from "@/utils/url";

export const SectionAction = ({
  nextStep,
  trigger,
  data,
  loading,
  emailWithoutMask,
  analyticsDetailAction,
  getFieldState,
}) => {
  const { digitalDataProductName, productName, homePath } = getProductOptions();
  const { dispatch: dispatchAuditory } = useAuditory();
  const dispatch = useDispatch();

  const { userData, leadData, idLead } = useSelector((state) => state.vida);
  const { planSelected, addThirdSuccess } = useSelector((state) => state.plans);
  const { state } = useSelector((state) => state.global);
  const { session } = useSelector((state) => state.token);
  const domicilio = useSelector((state) => state.domicilio);
  const { departamentos, provincias, distritos, validUbigeo } = domicilio;
  const { showModalAddBeneficiaries } = useSelector(
    (state) => state.beneficiaries
  );

  const [clickOnNextButton, setClickOnNextButton] = useState(false);

  const ideDepartamento = data?.department?.substring(1);
  const ideProvincia = data?.province?.substring(1);
  const ideDistrito = data?.district?.substring(1);
  const ubigeo = `${ideDepartamento}${ideProvincia}${ideDistrito}`;

  const currentStreetType = LIST_TYPE_ROAD.find(
    (item) => item.value === data?.streetType
  );

  const { sendLeads } = useLeadsService();
  const { getToken } = usePaymentService();
  const { endorseQuoteVE } = useQuoteService();

  const currentDepartment = departamentos.find(
    (item) => item.value === data?.department
  );
  const currentProvince = provincias.find(
    (item) => item.value === data?.province
  );
  const currentDistrict = distritos.find(
    (item) => item.value === data?.district
  );
  const hasTaxObligations = data?.hasTaxObligations === "Si";

  useEffect(() => {
    dispatch(showModalLoading(""));
  }, []);

  const updateConfigLog = () => {
    const typeDocument = String(userData.typeDoc);
    const auditory = outAuditory?.state?.auditoria;
    const configAuditory = {
      tipoDocumento:
        typeDocument.length === 1 ? `0${typeDocument}` : typeDocument,
      numeroDocumento: String(userData.documento),
      correo: emailWithoutMask || auditory?.correo,
      telefono: data?.phone || auditory?.telefono,
      producto: "VIDA",
      codigoProducto: auditory?.codigoProducto,
      codigoExterno: userData ? userData?.tercero : auditory?.codigoExterno,
      idLead: leadData ? leadData.idLead : auditory?.idLead,
      canal: "Portal Web",
      origen: auditory?.origen,
      proteccionDatos: auditory?.proteccionDatos,
      comunicacionComercial: auditory?.comunicacionComercial,
      pasoInicialFinalizado: 1,
    };
    logsConfig(dispatchAuditory, configAuditory);
  };

  const getPaymentToken = async () => {
    const payloadToken = {
      producto: "VCT",
      ideCotizacion: planSelected.idCotizacion,
      correo: emailWithoutMask,
      idePlan: String(planSelected.planId),
    };
    await getToken(payloadToken);
  };

  useEffect(() => {
    if (state === StateMachine[4]) {
      return dispatch(setViewModalError(true));
    }
    const functionOfEffect = async () => {
      const leadToSend = {
        ...leadData,
        idLead,
        finalizado: 4,
        tributaExtranjero: helpers.convertToSafeString(data?.hasTaxObligations).toUpperCase(),
      };
      if (hasTaxObligations) {
        dispatch(saveLeadData(leadToSend));
        sendLeads(leadToSend);
        helpers.getTagError("ErrorVidaEndosable", "Usuario presenta obligaciones tributarias", "trackError");
        helpers.getTagMetadata('Endpoint', url.registerLead, 'trackMetadata');
        return history.push(`${homePath}/gracias-validacion`);
      }

      const splitName = userData.nombres.split(" ");
      const firstName = splitName.map((item) => helpers.capitalizeString(item));
      const userAddress = {
        ubigeo,
        nombreVia: data?.address,
        tipoVia: currentStreetType,
        numeroVia: data?.streetNumber,
      };
      const newUserData = {
        ...userData,
        names: firstName.join(" "),
        apePaterno: helpers.capitalizeString(userData.apellidoPaterno),
        apeMaterno: helpers.capitalizeString(userData.apellidoMaterno),
        email: emailWithoutMask,
        correo: emailWithoutMask,
        telefono: data?.phone,
        userAddress,
      };
      dispatch(saveUSerData(newUserData));

      const newDomicilioData = {
        ...domicilio,
        validUbigeo,
        selectedDepartamento: currentDepartment,
        selectedProvincia: currentProvince,
        selectedDistrito: currentDistrict,
        departamento: currentDepartment,
        provincia: currentProvince,
        distrito: currentDistrict,
        nombreVia: data?.address,
        tipoVia: currentStreetType,
        numeroVia: data?.streetNumber,
      };
      dispatch(setBeneficiaryType(data?.beneficiaryType));
      dispatch(selectDomicilio(newDomicilioData));

      if (data?.beneficiaryType === "beneficiary-custom") return;

      dispatch(saveLeadData(leadToSend));
      await sendLeads(leadToSend);

      await endorseQuoteVE({
        userData: newUserData,
        domicilioData: newDomicilioData,
      });
      await getPaymentToken();
    };
    if (clickOnNextButton) {
      functionOfEffect();
    }
  }, [state, clickOnNextButton]);

  useEffect(() => {
    if (state !== StateMachine[4] && clickOnNextButton) {
      dispatch(setJobSelected(data?.job));
      dispatch(setWorkStatus(data?.isWorking));
      dispatch(setCompanyName(data?.company));
      dispatch(setHasTaxObligations(data?.hasTaxObligations));
      dispatch(setHasAcceptedExclusions(data?.hasAcceptedExclusions));

      if (
        session &&
        addThirdSuccess &&
        data?.beneficiaryType === "beneficiary-legal"
      ) {
        dispatch(showModalLoading(""));
        dispatch(updateBeneficiaries([]));
        history.push(nextStep);
      }

      if (data?.beneficiaryType === "beneficiary-custom") {
        setTimeout(() => {
          dispatch(showModalLoading(""));
          setClickOnNextButton(false);
          dispatch(showModalAddBeneficiariesAction(true));
          helpers.getTagVirtual(
            "sol:comprar:seguro-vida-endoso-con-devolucion:beneficiario"
          );
        }, 3000);
      }
    }
  }, [
    state,
    addThirdSuccess,
    session,
    clickOnNextButton,
    data?.beneficiaryType,
  ]);

  const onHandleClick = async () => {
    const result = await trigger();
    if (!result) return focusFirstErrorField({ getFieldState });

    updateConfigLog();

    const isLegalBeneficiary = data?.beneficiaryType === "beneficiary-legal";
    const isCustomBeneficiary = data?.beneficiaryType === "beneficiary-custom";
    const modalLoadingMessage = isLegalBeneficiary
      ? "No olvides tener tu tarjeta de pago a la mano"
      : "Asegúrate de registrar los datos correctos de tus beneficiarios";

    if (!hasTaxObligations) {
      dispatch(showModalLoading(modalLoadingMessage));
    }

    const tagEvents = [
      currentDepartment && {
        type: "getTagMetadata",
        key: `Dirección ${digitalDataProductName}`,
        value: helpers.capitalizeString(currentDepartment?.text),
      },
      {
        type: "getTagMetadata",
        key: `Datos Laborales ${digitalDataProductName}`,
        value: data?.isWorking,
      },
      {
        type: "getTagMetadata",
        key: `Obligaciones tributarias ${digitalDataProductName}`,
        value: data?.hasTaxObligations,
      },
      {
        type: "getTagMetadata",
        key: `Agregar beneficiarios ${digitalDataProductName}`,
        value: isLegalBeneficiary ? "Herederos legales" : "Elige a tus beneficiarios",
      },
      isLegalBeneficiary && {
        type: "getTagVirtual",
        name: `sol:comprar:${productName}:loader-pago`,
      },
      isCustomBeneficiary && {
        type: "getTagVirtual",
        name: `sol:comprar:${productName}:loader-beneficiario`,
      }
    ].filter(Boolean);

    sendTagChain(tagEvents);
    setClickOnNextButton(true);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Enter" && !showModalAddBeneficiaries) {
        event.preventDefault();
        onHandleClick();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showModalAddBeneficiaries]);

  return (
    <FooterAction
      textButton="Continuar"
      loading={clickOnNextButton || loading}
      onHandleClick={onHandleClick}
      contentAdditional={<DetailPlanMobile callAnalyticsAction={analyticsDetailAction} />}
    />
  );
};
